import { FiFolder, FiLoader, FiMoreVertical } from "react-icons/fi";
import type { GetFoldersQuery } from "../../../generated/graphql";
import { useAppDispatch } from "../../../store";
import { navigateToFolder } from "../../../store/slices/mediaSlice";

type FolderNode = NonNullable<GetFoldersQuery["folders"]["edges"][number]>["node"];

interface FolderCardProps {
  folder: FolderNode;
  isSelected?: boolean;
  isLoading?: boolean;
  onToggleSelect?: () => void;
  onContextMenu?: (e: React.MouseEvent) => void;
  onClick?: () => void;
}

export function FolderCard({
  folder,
  isSelected,
  isLoading,
  onToggleSelect,
  onContextMenu,
  onClick,
}: FolderCardProps) {
  const dispatch = useAppDispatch();

  const handleClick = () => {
    if (onClick) {
      onClick();
    } else {
      dispatch(navigateToFolder({ folderId: folder.id, folderName: folder.name }));
    }
  };

  const handleCheckboxClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onToggleSelect?.();
  };

  const handleContextMenuClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onContextMenu?.(e);
  };

  const handleMoreClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onContextMenu?.(e);
  };

  return (
    <div
      className={`${isSelected ? "ring-2 ring-blue-500" : ""} ${
        isLoading ? "opacity-60 pointer-events-none" : ""
      } group relative cursor-pointer rounded-lg border border-gray-200 bg-white p-3 transition-all hover:border-blue-300 hover:shadow-sm`}
      onClick={handleClick}
      onContextMenu={handleContextMenuClick}
    >
      {/* Selection Checkbox */}
      {onToggleSelect && (
        <div className="absolute left-2 top-2 z-10" onClick={handleCheckboxClick}>
          <input
            type="checkbox"
            checked={isSelected}
            onChange={() => {}}
            className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            aria-label={`Select ${folder.name}`}
          />
        </div>
      )}

      {/* Context Menu Button */}
      {onContextMenu && (
        <button
          type="button"
          onClick={handleMoreClick}
          className="absolute right-2 top-2 z-10 rounded p-1 opacity-0 hover:bg-gray-100 group-hover:opacity-100"
          aria-label="More options"
        >
          <FiMoreVertical className="h-4 w-4 text-gray-600" />
        </button>
      )}

      {/* Horizontal Layout: Icon and Text Side by Side */}
      <div className="flex items-center gap-3">
        {/* Folder Icon */}
        <div className="flex-shrink-0">
          {isLoading ? (
            <FiLoader className="h-10 w-10 text-blue-500 animate-spin" aria-hidden="true" />
          ) : (
            <FiFolder className="h-10 w-10 text-blue-500" aria-hidden="true" />
          )}
        </div>

        {/* Folder Name and Info */}
        <div className="flex-1 min-w-0">
          <h3 className="truncate text-sm font-medium text-gray-900" title={folder.name}>
            {folder.name}
          </h3>
          <p className="text-xs text-gray-500">
            {/* TODO: CHANGE IT INTO TOTAL ITEMS INSIDE THE FOLDER */}
            Folder
          </p>
        </div>
      </div>
    </div>
  );
}
